import { Building2, ChevronDown, Search } from 'lucide-react';
import { useMemo, useState } from 'react';

import { formatarDocumento } from '@/lib/formato';
import { normalizarBusca } from '@/lib/texto';
import { Button } from '@/ui/primitives/button';
import { Checkbox } from '@/ui/primitives/checkbox';
import { Popover } from '@/ui/primitives/popover';

import { type OpcaoEmpresa } from './tipos';

interface SeletorEmpresasProps {
  empresas: readonly OpcaoEmpresa[];
  /** Vazio = todas. */
  selecionadas: readonly string[];
  aoMudar: (ids: string[]) => void;
}

function resumo(empresas: readonly OpcaoEmpresa[], selecionadas: readonly string[]): string {
  if (selecionadas.length === 0 || selecionadas.length === empresas.length)
    return 'Todas as empresas';
  if (selecionadas.length === 1)
    return empresas.find((e) => e.id === selecionadas[0])?.nome ?? '1 empresa';
  return `${selecionadas.length} empresas`;
}

/** Nome contém o termo, ou — se o termo tiver dígitos/letras de CNPJ — o documento contém. */
function filtrarEmpresas(
  empresas: readonly OpcaoEmpresa[],
  busca: string,
): readonly OpcaoEmpresa[] {
  const termo = normalizarBusca(busca.trim());
  if (!termo) return empresas;
  const documento = termo.replace(/[^0-9a-z]/g, '').toUpperCase();
  return empresas.filter(
    (e) =>
      normalizarBusca(e.nome).includes(termo) ||
      (documento.length >= 3 && e.documento.includes(documento)),
  );
}

/** Escopo de trabalho: uma, várias ou todas as empresas permitidas. Vale para todas as telas. */
export function SeletorEmpresas({ empresas, selecionadas, aoMudar }: SeletorEmpresasProps) {
  const [busca, setBusca] = useState('');
  const visiveis = useMemo(() => filtrarEmpresas(empresas, busca), [empresas, busca]);
  const marcada = (id: string) => selecionadas.length === 0 || selecionadas.includes(id);

  const alternar = (id: string, valor: boolean) => {
    const base = selecionadas.length === 0 ? empresas.map((e) => e.id) : [...selecionadas];
    const proximas = valor ? [...base, id] : base.filter((x) => x !== id);
    aoMudar(proximas.length === empresas.length ? [] : proximas);
  };

  return (
    <Popover
      alinhamento="start"
      className="w-80 p-0"
      gatilho={
        <Button
          variante="secundario"
          className="max-w-64 justify-between"
          aria-label={`Empresas: ${resumo(empresas, selecionadas)}`}
        >
          <Building2 aria-hidden className="text-marca" />
          <span className="truncate">{resumo(empresas, selecionadas)}</span>
          <ChevronDown aria-hidden className="text-texto-fraco" />
        </Button>
      }
    >
      <div className="flex items-center gap-2 border-b border-borda px-3">
        <Search className="size-4 text-texto-fraco" aria-hidden />
        <input
          value={busca}
          onChange={(evento) => {
            setBusca(evento.target.value);
          }}
          placeholder="Buscar por nome ou CNPJ"
          aria-label="Buscar empresa"
          className="h-10 w-full bg-transparent text-base outline-none placeholder:text-texto-fraco"
        />
      </div>
      <div className="max-h-72 overflow-y-auto p-2">
        {visiveis.map((empresa) => (
          <Checkbox
            key={empresa.id}
            className="rounded-controle px-2 py-1.5 hover:bg-superficie-hover"
            marcado={marcada(empresa.id)}
            aoMudar={(valor) => {
              alternar(empresa.id, valor);
            }}
            rotulo={empresa.nome}
            descricao={<span className="font-mono">{formatarDocumento(empresa.documento)}</span>}
          />
        ))}
        {visiveis.length === 0 && (
          <p className="px-2 py-4 text-center text-sm text-texto-fraco">
            Nenhuma empresa encontrada.
          </p>
        )}
      </div>
      <div className="flex justify-between border-t border-borda p-2">
        <Button
          variante="fantasma"
          tamanho="sm"
          onClick={() => {
            aoMudar([]);
          }}
        >
          Todas
        </Button>
        <span className="self-center pr-2 text-xs text-texto-fraco">
          {empresas.length} disponíveis
        </span>
      </div>
    </Popover>
  );
}
